import type { ComposableDefinition } from "shared/composable-definition";

/**
 * Shape of modules returned by the Vite glob import.
 *
 * Each definition module must export `definition: ComposableDefinition`.
 */
interface ComposableDefinitionModule {
  readonly definition: ComposableDefinition;
}

/**
 * Lazily imported composable definition modules.
 */
const modules = import.meta.glob<ComposableDefinitionModule>("../composables/**/*.definition.ts");

/**
 * Converts a composable name into a URL-friendly slug.
 *
 * Examples:
 * - "usePosition" -> "use-position"
 * - "useStateStore" -> "use-state-store"
 */
const toComposableSlug = (name: string): string => {
  return name.replaceAll(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
};

/**
 * All collected {@link ComposableDefinition} instances.
 *
 * Starts empty — call {@link initComposableDefinitions} during bootstrap to populate.
 */
const _definitions: ComposableDefinition[] = [];
export const composableDefinitions: ReadonlyArray<ComposableDefinition> = _definitions;

/**
 * Map of composable slug → {@link ComposableDefinition}.
 *
 * Starts empty — call {@link initComposableDefinitions} during bootstrap to populate.
 */
const _definitionsBySlug: Record<string, ComposableDefinition> = {};
export const composableDefinitionsBySlug: Readonly<Record<string, ComposableDefinition>> = _definitionsBySlug;

/**
 * Resolves all composable definition modules in parallel and populates the
 * {@link composableDefinitions} and {@link composableDefinitionsBySlug} collections.
 *
 * Safe to call multiple times — subsequent calls are no-ops.
 */
export const initComposableDefinitions = async (): Promise<void> => {
  if (_definitions.length > 0) return;

  const resolved = await Promise.all(Object.values(modules).map((m) => m()));

  _definitions.push(...resolved.map((m) => m.definition));

  for (const def of _definitions) {
    if (def.name) {
      _definitionsBySlug[toComposableSlug(def.name)] = def;
    }
  }
};
